import type { InboxTextEntity } from "@bing/shared";
import { Fragment, type ReactNode } from "react";

/**
 * Render a broadcast message's `text` with its sanitized formatting `entities` (bold/italic/
 * underline/strikethrough/code/link) as REACT NODES — never an HTML string, so there is zero
 * injection surface even though the content is admin-authored.
 *
 * Telegram entity offsets are UTF-16 code units, which is exactly how a JS string is indexed, so
 * `text.slice(offset, offset+length)` reproduces each range. Ranges can OVERLAP/NEST (e.g. bold+
 * italic on the same span), so we sweep the boundary points: between two adjacent boundaries the
 * set of active entities is constant, and we wrap that slice in the corresponding nested tags.
 * Newlines survive because the caller renders inside a `whitespace-pre-wrap` container.
 */
export function RichText({
  text,
  entities,
}: {
  text: string;
  entities: readonly InboxTextEntity[];
}): ReactNode {
  if (entities.length === 0) return text;

  // Every distinct start/end boundary, plus the text ends, sorted ascending.
  const bounds = new Set<number>([0, text.length]);
  for (const e of entities) {
    bounds.add(Math.max(0, e.offset));
    bounds.add(Math.min(text.length, e.offset + e.length));
  }
  const points = [...bounds].sort((a, b) => a - b);

  const nodes: ReactNode[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i]!;
    const end = points[i + 1]!;
    if (start >= end) continue;
    const slice = text.slice(start, end);
    // Entities fully covering this segment (offset ≤ start AND end ≤ offset+length).
    const active = entities.filter((e) => e.offset <= start && e.offset + e.length >= end);
    nodes.push(<Fragment key={i}>{wrap(slice, active)}</Fragment>);
  }
  return nodes;
}

/** Wrap `text` in the nested inline tags for the `active` entities (styles inner, link outermost). */
function wrap(text: string, active: readonly InboxTextEntity[]): ReactNode {
  let node: ReactNode = text;
  for (const e of active) {
    switch (e.type) {
      case "bold":
        node = <strong className="font-bold">{node}</strong>;
        break;
      case "italic":
        node = <em>{node}</em>;
        break;
      case "underline":
        node = <u>{node}</u>;
        break;
      case "strikethrough":
        node = <s>{node}</s>;
        break;
      case "code":
        node = (
          <code className="rounded bg-surface-container-highest px-1 font-mono text-[0.92em]">
            {node}
          </code>
        );
        break;
      // text_link is applied last (outermost) below.
      default:
        break;
    }
  }
  const link = active.find((e) => e.type === "text_link");
  if (link?.url) {
    node = (
      <a
        href={link.url}
        target="_blank"
        rel="noreferrer noopener"
        className="text-secondary underline underline-offset-2"
      >
        {node}
      </a>
    );
  }
  return node;
}
